import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Star } from 'lucide-react';
import { Badge, Button, Card, CardContent } from '@mlm/ui';
import {
  listOfficialStoreReviews,
  productReviewPhotoUrl,
  setOfficialStoreReviewFeatured,
  type OfficialStoreReview,
} from '../lib/api';

/**
 * Official Store Featured Feedback. Admin pins reviews of HOUSE products (no
 * merchant) to the "Featured Feedback" section on the Official Store page. No
 * cap. Mirrors the merchant Seller-Center feedback manager. Pinning requires
 * `product.write`; viewing requires `view.products.catalog`.
 */
export function OfficialFeedbackPage() {
  const qc = useQueryClient();
  const [featuredOnly, setFeaturedOnly] = useState(false);

  const { data: reviews = [], isLoading } = useQuery({
    queryKey: ['official-store-reviews', featuredOnly],
    queryFn: () => listOfficialStoreReviews({ featuredOnly: featuredOnly || undefined }),
  });

  const toggle = useMutation({
    mutationFn: (r: OfficialStoreReview) => setOfficialStoreReviewFeatured(r.id, !r.featured),
    meta: { successMessage: 'Featured status updated.' },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['official-store-reviews'] }),
  });

  const featuredCount = reviews.filter((r) => r.featured).length;

  return (
    <div className="space-y-4">
      <div className="flex items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold">Official Store Feedback</h1>
          <p className="text-sm text-muted-foreground">
            Pin reviews of Official Store (house) products to the &ldquo;Featured Feedback&rdquo;
            section of the Official Store page. No limit.
          </p>
        </div>
        <label className="flex shrink-0 items-center gap-2 text-sm text-muted-foreground">
          <input
            type="checkbox"
            checked={featuredOnly}
            onChange={(e) => setFeaturedOnly(e.target.checked)}
          />
          Featured only{!featuredOnly && featuredCount > 0 ? ` (${featuredCount})` : ''}
        </label>
      </div>

      {isLoading ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : reviews.length === 0 ? (
        <Card>
          <CardContent className="py-10 text-center text-sm text-muted-foreground">
            {featuredOnly
              ? 'No featured reviews yet.'
              : 'No reviews on Official Store products yet.'}
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {reviews.map((r) => (
            <Card key={r.id}>
              <CardContent className="flex items-start gap-4 py-4">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <Stars rating={r.rating} />
                    {r.featured && <Badge>Featured</Badge>}
                    <span className="ml-auto text-xs text-muted-foreground">
                      {new Date(r.createdAt).toLocaleDateString()}
                    </span>
                  </div>
                  <div className="mt-1 text-xs text-muted-foreground">
                    {r.reviewerName} · <span className="opacity-70">{r.productName}</span>
                  </div>
                  {r.comment && <p className="mt-1.5 text-sm">{r.comment}</p>}
                  {r.hasPhoto && (
                    <div className="mt-2 flex gap-1.5">
                      {Array.from({ length: r.photoCount }).map((_, i) => (
                        <img
                          key={i}
                          src={productReviewPhotoUrl(r.productId, r.id, i)}
                          alt=""
                          className="h-14 w-14 rounded object-cover"
                        />
                      ))}
                    </div>
                  )}
                </div>
                <Button
                  variant={r.featured ? 'outline' : 'default'}
                  size="sm"
                  disabled={toggle.isPending}
                  onClick={() => toggle.mutate(r)}
                >
                  {r.featured ? 'Unfeature' : 'Feature'}
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

function Stars({ rating }: { rating: number }) {
  return (
    <span className="inline-flex" aria-label={`${rating} star${rating === 1 ? '' : 's'}`}>
      {[1, 2, 3, 4, 5].map((n) => (
        <Star
          key={n}
          className={`h-4 w-4 ${n <= rating ? 'fill-amber-400 text-amber-400' : 'text-muted-foreground/30'}`}
        />
      ))}
    </span>
  );
}
